import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import React, { useState } from 'react';
import formStyles from 'legacy/common/components/form/Form.module.scss';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import { useTextAllGuests } from 'legacy/events/hooks';
import { showModal } from 'legacy/actions/modalActions';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import classnames from 'classnames';

const MAX_MESSAGE_LENGTH = 240;

const TextAllGuestsSchema = Yup.object().shape({
  message: Yup.string()
    .required('Message is required')
    .max(
      MAX_MESSAGE_LENGTH,
      `Message must be ${MAX_MESSAGE_LENGTH} characters or less`
    )
});

type TextAllGuestsProps = {
  eventId: string;
  onClose: () => void;
  visible: boolean;
};

const TextAllGuestsModal: React.FC<TextAllGuestsProps> = ({
  eventId,
  onClose,
  visible
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const dispatch = useDispatch();
  const textAllGuests = useTextAllGuests();

  return (
    <Modal
      visible={visible}
      type={ModalType.VERY_SMALL}
      onClose={onClose}
      minHeight="500px"
    >
      <div className="h-full flex flex-col py-12 px-10">
        <h1 className="flex-shrink-0 text-2xl text-gray-500 font-normal">
          Text all guests
        </h1>
        <p className="font-bold mt-8">
          You&apos;re about to send an SMS to all your guests!
        </p>
        <p className="mt-4">
          Have you considered using the event post feed for a more interactive
          announcement?
        </p>
        <Formik
          validationSchema={TextAllGuestsSchema}
          initialValues={{
            message: ''
          }}
          onSubmit={({ message }) => {
            setIsLoading(true);
            textAllGuests.mutate(
              { eventId, message },
              {
                onSuccess: () => {
                  setIsLoading(false);
                  setIsComplete(true);
                },
                onError: (error) => {
                  dispatch(
                    showModal('ALERT', {
                      message: error?.message
                    })
                  );
                  setIsLoading(false);
                }
              }
            );
          }}
        >
          {({ errors, touched, values, setFieldValue }) => (
            <Form className={formStyles.container}>
              <div className={formStyles.group}>
                <div className={formStyles.label}>Message</div>
                <Field
                  id="message"
                  name="message"
                  as="textarea"
                  placeholder="Enter your message"
                  className={classnames(
                    formStyles.textarea,
                    'h-56 resize-none',
                    {
                      [formStyles.errorField]: errors.message && touched.message
                    }
                  )}
                  disabled={isComplete}
                  maxLength={MAX_MESSAGE_LENGTH}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    const message = e.target.value;
                    setFieldValue('message', message);
                  }}
                  value={values.message}
                />
                <div className="text-gray-500 text-sm text-right">
                  {MAX_MESSAGE_LENGTH - values.message.length} characters
                  remaining
                </div>
                {errors.message && touched.message && (
                  <div className={formStyles.error}>{errors.message}</div>
                )}
              </div>

              <div className={formStyles.button}>
                <Button
                  isSubmit
                  loading={isLoading}
                  disabled={isComplete}
                  type={ButtonType.Primary}
                >
                  {isComplete ? 'SMS Sent' : 'Send SMS'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
};

export default TextAllGuestsModal;
